import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { AlertTriangle, ShoppingCart, Tag, RefreshCw, ArrowRight, HelpCircle } from 'lucide-react';
import { wheelSteps, wheelPitfalls } from '../mock';
import SEO, { generateArticleSchema, generateBreadcrumbSchema, generateHowToSchema, generateFAQSchema, generateVideoSchema } from '../components/SEO';
import AdSense from '../components/AdSense';
import LiteYouTube from '../components/LiteYouTube';

const iconMap = {
  ShoppingCart,
  Tag,
  RefreshCw
};

const Wheel = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wheel Options Strategy Explained Simply (Wholesale-Retail Analogy)"
        description="Learn the wheel strategy in plain English: sell puts → own shares → sell calls → repeat. Pros, cons, and is it safe for beginners? Complete guide."
        keywords="wheel strategy explained, wheel options strategy for beginners, what is the wheel strategy, wheel strategy pros and cons, is wheel strategy safe, options wheel explained simply"
        canonical="https://optionsanalogies.com/wheel"
        type="article"
        article={true}
        publishedTime="2025-01-15T00:00:00Z"
        modifiedTime="2025-01-15T00:00:00Z"
        schema={[
          generateArticleSchema({
            title: "The Wheel Strategy: Step-by-Step Guide for Income Generation",
            description: "Master the options wheel strategy with this complete guide covering CSPs, covered calls, and how to repeat the cycle.",
            url: "https://optionsanalogies.com/wheel",
            datePublished: "2025-01-15T00:00:00Z",
            dateModified: "2025-01-15T00:00:00Z",
            image: "https://optionsanalogies.com/og-wheel.png"
          }),
          generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "The Wheel Strategy", path: "/wheel" }
          ]),
          generateHowToSchema({
            name: "How to Run The Wheel Strategy",
            description: "A step-by-step guide to implementing the wheel strategy for options income generation",
            totalTime: "PT30M",
            steps: [
              {
                name: "Sell Cash-Secured Put",
                description: "Get paid to wait for shares at your target price",
                url: "/csp"
              },
              {
                name: "Get Assigned Shares",
                description: "Acquire shares at the strike price if assigned",
                url: "/wheel"
              },
              {
                name: "Sell Covered Call",
                description: "Get paid to sell your shares at a higher price",
                url: "/covered-calls"
              }
            ]
          }),
          generateFAQSchema([
            {
              question: "What is the wheel strategy in simple terms?",
              answer: "The wheel strategy is like running a wholesale-to-retail shop: you get paid to stock inventory (sell cash-secured puts), then get paid again to sell it (sell covered calls). If your shares get called away, you restart the cycle. It's a repeatable process for generating income."
            },
            {
              question: "Is the wheel strategy safe for beginners?",
              answer: "The wheel is considered more conservative than many options strategies, but it still carries significant risk. You can lose money if stocks drop during assignment or underperform buy-and-hold in bull markets. Beginners should understand both CSPs and covered calls individually before attempting the wheel."
            },
            {
              question: "What are the main risks of the wheel strategy?",
              answer: "Main risks include: getting assigned on falling stocks (locking in losses), missing big upward moves when shares are called away, requiring significant capital per position, and potentially underperforming simple buy-and-hold during strong bull markets."
            },
            {
              question: "How much money do I need to run the wheel strategy?",
              answer: "You need enough capital to buy 100 shares at your chosen strike price. For a $50 strike, that's $5,000 per position. Most traders run the wheel on multiple stocks, so $10,000-$25,000 is a practical minimum to diversify appropriately."
            },
            {
              question: "Can you lose money with the wheel strategy?",
              answer: "Yes. If stocks decline significantly after you're assigned shares, you'll have losses even after premium collection. The wheel doesn't protect against bear markets or stock-specific collapses. It's income generation, not loss prevention."
            }
          ]),
          generateVideoSchema({
            name: "The Wheel Strategy Explained",
            description: "Complete guide to running the Wheel Strategy for options income generation. Learn how to combine cash-secured puts and covered calls in a repeatable cycle.",
            uploadDate: "2025-01-15",
            thumbnailUrl: "https://img.youtube.com/vi/_FyrLUcJeDE/maxresdefault.jpg",
            contentUrl: "https://www.youtube.com/watch?v=_FyrLUcJeDE"
          })
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'The Wheel Strategy', path: '/wheel' }]} />

        {/* Header - Ad-Free Zone */}
        <div className="mb-12" data-nosnippet>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Wheel Strategy
          </h1>
          <p className="text-2xl text-emerald-500 font-medium mb-4">
            "A wholesale-to-retail shop: get paid to stock inventory, then get paid to sell it."
          </p>
          <p className="text-lg text-gray-300">
            A repeatable cycle: Sell CSP → (if assigned) hold shares → Sell CC → (if called) repeat.
          </p>
        </div>

        {/* Video Explanation - Ad-Free Zone */}
        <section className="mb-16" data-nosnippet>
          <div className="glass rounded-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Watch: The Wheel Strategy Explained</h2>
            <div className="rounded-lg overflow-hidden border border-emerald-500/20 shadow-lg">
              <LiteYouTube 
                videoId="_FyrLUcJeDE"
                title="The Wheel Strategy Explained"
              />
              <div className="bg-gray-800/50 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-400">Complete guide to running the Wheel Strategy</span>
                <a 
                  href="https://youtu.be/_FyrLUcJeDE" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                >
                  <span>Watch on YouTube</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How it Flows */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">How The Wheel Flows</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {wheelSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <Card key={step.id} className="bg-gray-900/50 border-gray-800 relative">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {index + 1}
                  </div>
                  <CardHeader className="pt-8">
                    <Icon className="w-10 h-10 text-emerald-500 mb-3" />
                    <CardTitle className="text-white">{step.step}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Visual Flow */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">The Cycle</h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="text-center">
                <div className="w-32 h-32 bg-emerald-600/20 border-2 border-emerald-600 rounded-full flex items-center justify-center mb-3">
                  <div className="text-center">
                    <p className="text-emerald-400 font-bold text-lg">CSP</p>
                    <p className="text-xs text-gray-400">Get Paid<br />to Wait</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400">Sell Cash-Secured Put</p>
              </div>
              
              <ArrowRight className="w-8 h-8 text-emerald-500 transform md:rotate-0 rotate-90" />
              
              <div className="text-center">
                <div className="w-32 h-32 bg-blue-600/20 border-2 border-blue-600 rounded-full flex items-center justify-center mb-3">
                  <div className="text-center">
                    <p className="text-blue-400 font-bold text-lg">Assigned</p>
                    <p className="text-xs text-gray-400">Own<br />Shares</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400">Acquire Stock</p>
              </div>
              
              <ArrowRight className="w-8 h-8 text-emerald-500 transform md:rotate-0 rotate-90" />
              
              <div className="text-center">
                <div className="w-32 h-32 bg-emerald-600/20 border-2 border-emerald-600 rounded-full flex items-center justify-center mb-3">
                  <div className="text-center">
                    <p className="text-emerald-400 font-bold text-lg">CC</p>
                    <p className="text-xs text-gray-400">Get Paid<br />to Sell</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400">Sell Covered Call</p>
              </div>
              
              <ArrowRight className="w-8 h-8 text-emerald-500 transform md:rotate-0 rotate-90" />
              
              <div className="text-center">
                <div className="w-32 h-32 bg-purple-600/20 border-2 border-purple-600 rounded-full flex items-center justify-center mb-3">
                  <div className="text-center">
                    <p className="text-purple-400 font-bold text-lg">Called</p>
                    <p className="text-xs text-gray-400">Shares<br />Sold</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400">Repeat Cycle</p>
              </div>
            </div>
          </div>
        </section>

        {/* Ad Placement 1 - After Video */}
        <section className="mb-16">
          <AdSense slot="4646845104" format="horizontal" />
        </section>

        {/* Ad Placement 2 - After Flow Diagram */}
        <section className="mb-16">
          <AdSense slot="6630828859" format="rectangle" />
        </section>

        {/* Strengths & Weaknesses */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Strengths & Weaknesses</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gray-900/50 border-emerald-800">
              <CardHeader>
                <CardTitle className="text-emerald-400">Wheel Strengths</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <p className="text-gray-300">Systematic entries and exits reduce emotional trading</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <p className="text-gray-300">Steady premium collection on both sides of ownership</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <p className="text-gray-300">Clear rules and process for repeatability</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <p className="text-gray-300">Works well in sideways or mildly trending markets</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-red-800">
              <CardHeader>
                <CardTitle className="text-red-400">Wheel Weaknesses</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 mt-1">✗</span>
                    <p className="text-gray-300">Downtrends can compound losses through assignment</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 mt-1">✗</span>
                    <p className="text-gray-300">Strong uptrends cap gains (shares called away early)</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 mt-1">✗</span>
                    <p className="text-gray-300">Requires significant capital per stock</p>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-500 mt-1">✗</span>
                    <p className="text-gray-300">Can underperform buy-and-hold in bull markets</p>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Common Pitfalls */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Common Pitfalls</h2>
          <div className="space-y-3">
            {wheelPitfalls.map((pitfall, index) => (
              <Alert key={index} className="border-amber-500/50 bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <AlertDescription className="text-gray-300 ml-2">
                  {pitfall}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        </section>

        {/* Ad Placement 3 - After Pitfalls */}
        <section className="mb-16">
          <AdSense slot="2292144504" format="horizontal" />
        </section>

        {/* Learn More */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Before You Try The Wheel - Master the Components First</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              The Wheel combines <Link to="/csp" className="text-emerald-400 hover:text-emerald-300 underline">cash-secured puts</Link> and <Link to="/covered-calls" className="text-emerald-400 hover:text-emerald-300 underline">covered calls</Link>. Understanding each strategy individually is crucial for successful implementation. Don't attempt the wheel until you're comfortable with both legs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" asChild>
                <Link to="/csp">Learn Cash-Secured Puts</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/covered-calls">Learn Covered Calls</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Beginner FAQs */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-emerald-500" />
              <h2 className="text-3xl font-bold text-white">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-6">
              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What is the wheel strategy in simple terms?</h3>
                <p className="text-gray-300 leading-relaxed">
                  The wheel strategy is like running a wholesale-to-retail shop: you get paid to stock inventory (sell cash-secured puts), then get paid again to sell it (sell covered calls). If your shares get called away, you restart the cycle. It's a repeatable process for generating income.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Is the wheel strategy safe for beginners?</h3>
                <p className="text-gray-300 leading-relaxed">
                  The wheel is considered more conservative than many options strategies, but it still carries significant risk. You can lose money if stocks drop during assignment or underperform buy-and-hold in bull markets. Beginners should understand both CSPs and covered calls individually before attempting the wheel.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What are the main risks of the wheel strategy?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Main risks include: getting assigned on falling stocks (locking in losses), missing big upward moves when shares are called away, requiring significant capital per position, and potentially underperforming simple buy-and-hold during strong bull markets.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">How much money do I need to run the wheel strategy?</h3>
                <p className="text-gray-300 leading-relaxed">
                  You need enough capital to buy 100 shares at your chosen strike price. For a $50 strike, that's $5,000 per position. Most traders run the wheel on multiple stocks, so $10,000-$25,000 is a practical minimum to diversify appropriately.
                </p>
              </div>

              <div className="pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Can you lose money with the wheel strategy?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Yes. If stocks decline significantly after you're assigned shares, you'll have losses even after premium collection. The wheel doesn't protect against bear markets or stock-specific collapses. It's income generation, not loss prevention.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Disclaimer */}
        <section className="mb-16">
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <AlertDescription className="text-gray-300 ml-2">
              <strong className="text-white">Education Only - Not Financial Advice:</strong> This content is for educational purposes only and does not constitute financial, tax, or legal advice. The wheel strategy involves significant risk and may not be suitable for all investors. You could lose your entire investment. Options trading requires substantial capital and carries the risk of assignment at unfavorable prices. Before trading options, carefully consider your financial situation, investment objectives, and risk tolerance. Consult with a qualified financial advisor before making any investment decisions.
            </AlertDescription>
          </Alert>
        </section>
      </div>
    </div>
  );
};

export default Wheel;